import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { openTelemetryStream } from "../../lib/telemetryStreamClient";
import { FleetOutageService } from "../services/FleetOutageService";
import { useFleetOutageStore } from "../../stores/useFleetOutageStore";
import { useOrganizationStore } from "../../stores/useOrganizationStore";
import { camelCaseObject } from "../../lib/utils";
import { createOutageNotificationHandler } from "../../lib/outageNotifications";
import { patchAdminDashboardOutageCache } from "../../lib/adminDashboardOutagePatch";
import { patchTelemetryOutagePageCache } from "../../lib/telemetryOutagePagePatch";
import type { FleetOutageEntry } from "../types";
import type { FleetOutageStoreEntry } from "../../stores/useFleetOutageStore";

function toInitialStoreEntry(item: {
  facilityId: number;
  orgId: number;
  isOngoing: boolean;
  outageWindowStart: string | null;
  causeAtDetection: FleetOutageEntry["causeAtDetection"];
  facilityName: string;
}): FleetOutageStoreEntry {
  return {
    facilityId: item.facilityId,
    orgId: item.orgId,
    isOngoing: item.isOngoing,
    outageWindowStart: item.outageWindowStart,
    causeAtDetection: item.causeAtDetection,
    facilityName: item.facilityName,
  };
}

/**
 * Fleet-wide (cross-facility) outage connection for platform admins — the
 * app-shell-mounted counterpart to the per-facility useTelemetryStream
 * (§2.3, §6 of the FE push architecture spec).
 *
 * Differs from useTelemetryStream in two load-bearing ways:
 * - No tab-visibility suspend/resume — this connection must stay open even
 *   while the tab is hidden, since admins need outage awareness regardless
 *   of which page (or app) currently has focus.
 * - The initial REST snapshot and the SSE connection are both owned here
 *   (useTelemetryStream leaves REST fetching to the individual facility
 *   pages) — there's no other consumer for fleet-wide outage state.
 *
 * `enabled` is required, not optional/defaulted, mirroring
 * useAvailableFacilityComponents's pattern — the caller (AppLayout) must
 * explicitly gate this on platform-admin role so a non-admin session never
 * opens the connection or fires the REST call at all.
 */
export function useFleetOutageStream(enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    const resolveFacilityName = (facilityId: number): string | undefined =>
      useOrganizationStore
        .getState()
        .organizations.flatMap((org) => org.facilities)
        .find((facility) => facility.id === facilityId)?.name;

    const toStoreEntry = (entry: FleetOutageEntry): FleetOutageStoreEntry => ({
      ...entry,
      facilityName: resolveFacilityName(entry.facilityId),
    });

    const handleOutageNotification = createOutageNotificationHandler();

    // Unlike the live event:outage push, the snapshot's items already
    // include facilityName (confirmed against the backend's local runbook,
    // JAA-1095) — no need for the local useOrganizationStore lookup here,
    // only for live pushes below. setInitialOutages fully replaces the
    // store's outages map, which is exactly right both for the first fetch
    // and for resyncing after a reconnect (below) — a fresh snapshot is
    // full current truth, correctly overwriting anything stale/missed.
    const fetchAndSeedOutages = () => {
      FleetOutageService.fetchFleetOutages()
        .then((response) => {
          useFleetOutageStore
            .getState()
            .setInitialOutages(response.items.map(toInitialStoreEntry));
        })
        .catch((error) => {
          // Previously swallowed silently, which made this failure mode
          // invisible — a broken fetch here means the admin starts (or
          // resyncs after a reconnect) with an empty/stale snapshot and
          // nothing else surfaces that fact (JAA-1095, found via BE review).
          console.error("Failed to fetch fleet outage snapshot", error);
        });
    };

    // Not awaited before opening the stream below — independent, not
    // causally linked, same "par" relationship the facility-page REST
    // fetch has with its own SSE connection (§2.2, §8.6). A failure here
    // just means the admin starts with an empty snapshot until the next
    // live event: outage push arrives.
    fetchAndSeedOutages();

    // Tracks whether the "live" state has already been reached once, so
    // the fetch above (which already covers the first connect) isn't
    // duplicated — only re-fetch on a genuine reconnect.
    let hasConnectedOnce = false;

    const teardown = openTelemetryStream<FleetOutageEntry | Record<string, unknown>>({
      url: "/api/v1/admin/telemetry/stream",
      onEvent: (eventType, data) => {
        if (eventType !== "outage") return;
        const normalized = camelCaseObject(data) as FleetOutageEntry;
        const storeEntry = toStoreEntry(normalized);
        useFleetOutageStore.getState().setOutage(storeEntry);
        handleOutageNotification(storeEntry);

        // Computed from the store (already updated above) rather than the
        // admin-dashboard cache itself — that cache's own org.facilities is
        // null on the real response, so it has no local way to know whether
        // a sibling facility in the same org still has an ongoing outage.
        // The fleet-wide store has full visibility across every facility,
        // seeded by the initial snapshot fetch and kept live from here.
        const orgHasAnyOngoingOutage = Object.values(
          useFleetOutageStore.getState().outages,
        ).some((entry) => entry.orgId === normalized.orgId && entry.isOngoing);

        patchAdminDashboardOutageCache(queryClient, {
          facilityId: normalized.facilityId,
          orgId: normalized.orgId,
          isOngoing: normalized.isOngoing,
          orgHasAnyOngoingOutage,
        });

        patchTelemetryOutagePageCache(queryClient, {
          facilityId: normalized.facilityId,
          isOngoing: normalized.isOngoing,
        });
      },
      onStateChange: (state) => {
        // No connection-state UI exists for the admin channel yet — the
        // transport itself already reconnects indefinitely with backoff.
        // But the stream never replays missed events (JAA-1095): without
        // this, a transient drop or the scheduled 30-min token-TTL close
        // would leave the admin's outage state — and everything it drives
        // (bell icon, Admin Dashboard badges, the Telemetry Outage page
        // banner) — silently stuck on whatever it was before the gap.
        if (state !== "live") return;
        if (hasConnectedOnce) {
          fetchAndSeedOutages();
          // Resyncing the fleet-wide store above only drives the bell icon
          // and Admin Dashboard badges — it doesn't independently catch a
          // resolve/open event missed during the gap for whichever
          // facility's Telemetry Outage page happens to be open (per BE
          // review). Broadly invalidating (no facilityId) is cheap:
          // invalidateQueries' default refetchType ("active") only fires a
          // REST call if some facility's outage page is actually mounted.
          queryClient.invalidateQueries({ queryKey: ["telemetryOutages"] });
        }
        hasConnectedOnce = true;
      },
    });

    return () => {
      teardown();
    };
    // queryClient is referentially stable (same context value across
    // renders) — included for correctness, not because it ever actually
    // changes and triggers a reconnect.
  }, [enabled, queryClient]);
}
